import { __extends } from "tslib";
import { BitmapImage2D } from '@awayjs/stage';
import { ImageTexture2D } from '@awayjs/renderer';
import { ShadowMethodBase, _Shader_ShadowMethodBase } from './ShadowMethodBase';
/**
 * ShadowDitheredMethod provides a soft shadowing technique by randomly distributing sample points differently for each fragment.
 */
var ShadowDitheredMethod = /** @class */ (function (_super) {
    __extends(ShadowDitheredMethod, _super);
    /**
     * Creates a new ShadowDitheredMethod object.
     * @param castingLight The light casting the shadows
     * @param numSamples The amount of samples to take for dithering. Minimum 1, maximum 24.
     */
    function ShadowDitheredMethod(castingLight, numSamples, range) {
        if (numSamples === void 0) { numSamples = 4; }
        if (range === void 0) { range = 1; }
        var _this = _super.call(this, castingLight) || this;
        _this._depthMapSize = _this._castingLight.shadowMapper.size;
        _this.numSamples = numSamples;
        _this.range = range;
        ++ShadowDitheredMethod._grainUsages;
        if (!ShadowDitheredMethod._grainTexture)
            _this.initGrainTexture();
        return _this;
    }
    Object.defineProperty(ShadowDitheredMethod.prototype, "assetType", {
        /**
         * @inheritDoc
         */
        get: function () {
            return ShadowDitheredMethod.assetType;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowDitheredMethod.prototype, "numSamples", {
        /**
         * The amount of samples to take for dithering. Minimum 1, maximum 24. The actual maximum may depend on the
         * complexity of the shader.
         */
        get: function () {
            return this._numSamples;
        },
        set: function (value) {
            if (value < 1)
                value = 1;
            else if (value > 24)
                value = 24;
            if (this._numSamples == value)
                return;
            this._numSamples = value;
            this.invalidateShaderProgram();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowDitheredMethod.prototype, "range", {
        /**
         * The range in the shadow map in which to distribute the samples.
         */
        get: function () {
            return this._range * 2;
        },
        set: function (value) {
            this._range = value / 2;
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Creates a texture containing the dithering noise texture.
     */
    ShadowDitheredMethod.prototype.initGrainTexture = function () {
        ShadowDitheredMethod._grainBitmapImage2D = new BitmapImage2D(64, 64, false);
        var vec /*uint*/ = new Array();
        var len /*uint*/ = 4096;
        var step = 1 / (this._depthMapSize * this._range);
        var r, g;
        for (var i /*uint*/ = 0; i < len; ++i) {
            r = 2 * (Math.random() - .5);
            g = 2 * (Math.random() - .5);
            if (r < 0)
                r -= step;
            else
                r += step;
            if (g < 0)
                g -= step;
            else
                g += step;
            if (r > 1)
                r = 1;
            else if (r < -1)
                r = -1;
            if (g > 1)
                g = 1;
            else if (g < -1)
                g = -1;
            vec[i] = (Math.floor((r * .5 + .5) * 0xff) << 16) | (Math.floor((g * .5 + .5) * 0xff) << 8);
        }
        ShadowDitheredMethod._grainBitmapImage2D.setArray(ShadowDitheredMethod._grainBitmapImage2D.rect, vec);
        ShadowDitheredMethod._grainTexture = new ImageTexture2D(ShadowDitheredMethod._grainBitmapImage2D);
    };
    /**
     * @inheritDoc
     */
    ShadowDitheredMethod.prototype.dispose = function () {
        if (--ShadowDitheredMethod._grainUsages == 0) {
            ShadowDitheredMethod._grainTexture.dispose();
            ShadowDitheredMethod._grainBitmapImage2D.dispose();
            ShadowDitheredMethod._grainTexture = null;
        }
    };
    ShadowDitheredMethod.assetType = '[asset ShadowDitheredMethod]';
    return ShadowDitheredMethod;
}(ShadowMethodBase));
export { ShadowDitheredMethod };
import { ShaderBase } from '@awayjs/renderer';
/**
 * _Shader_ShadowDitheredMethod provides a soft shadowing technique by randomly distributing sample points differently for each fragment.
 */
var _Shader_ShadowDitheredMethod = /** @class */ (function (_super) {
    __extends(_Shader_ShadowDitheredMethod, _super);
    /**
     * Creates a new _Shader_ShadowDitheredMethod.
     */
    function _Shader_ShadowDitheredMethod(method, shader) {
        return _super.call(this, method, shader) || this;
    }
    /**
     * @inheritDoc
     */
    _Shader_ShadowDitheredMethod.prototype._initVO = function (chunkVO) {
        _super.prototype._initVO.call(this, chunkVO);
        chunkVO.needsProjection = true;
        this._grainMap = ShadowDitheredMethod._grainTexture.getAbstraction(this._shader);
    };
    /**
     * @inheritDoc
     */
    _Shader_ShadowDitheredMethod.prototype._initConstants = function () {
        _super.prototype._initConstants.call(this);
        var data = this._shader.fragmentConstantData;
        var index = this._fragmentConstantsIndex;
        data[index] = 1 / this._method.numSamples;
    };
    /**
     * @inheritDoc
     */
    _Shader_ShadowDitheredMethod.prototype._activate = function () {
        _super.prototype._activate.call(this);
        var data = this._shader.fragmentConstantData;
        var index = this._fragmentConstantsIndex;
        data[index + 1] = (this._shader.renderMaterial.renderer.view.width - 1) / 63;
        data[index + 2] = (this._shader.renderMaterial.renderer.view.height - 1) / 63;
        data[index + 3] = 2 * this._method.range / this._method.castingLight.shadowMapper.size;
        this._grainMap.activate();
    };
    /**
     * @inheritDoc
     */
    _Shader_ShadowDitheredMethod.prototype._getFragmentCode = function (targetReg, regCache, sharedRegisters) {
        var dataReg = regCache.getFreeFragmentConstant();
        this._fragmentConstantsIndex = dataReg.index * 4;
        var code = '';
        var numSamples = this._method.numSamples;
        var uvReg = regCache.getFreeFragmentVectorTemp();
        regCache.addFragmentTempUsages(uvReg, 1);
        var temp = regCache.getFreeFragmentVectorTemp();
        regCache.addFragmentTempUsages(temp, 1);
        var projectionReg = sharedRegisters.projectionFragment;
        code += 'div ' + uvReg + ', ' + projectionReg + ', ' + projectionReg + '.w\n' +
            'mul ' + uvReg + '.xy, ' + uvReg + '.xy, ' + dataReg + '.yz\n';
        var index = numSamples;
        while (index > 0) {
            if (index == numSamples) {
                code += this._grainMap._getFragmentCode(uvReg, regCache, sharedRegisters, uvReg);
            }
            else {
                code += 'mov ' + temp + ', ' + uvReg + '.zwxy \n' +
                    this._grainMap._getFragmentCode(uvReg, regCache, sharedRegisters, temp);
            }
            // keep grain in uvReg.zw
            code += 'sub ' + uvReg + '.zw, ' + uvReg + '.xy, fc0.xx\n' + // uv-.5
                'mul ' + uvReg + '.zw, ' + uvReg + '.zw, ' + dataReg + '.w\n'; // (tex unpack scale and tex scale in one)
            if (index == numSamples) {
                // first sample
                code += 'add ' + uvReg + '.xy, ' + uvReg + '.zw, ' + this._baseChunk.depthMapCoordReg + '.xy\n' +
                    this._baseTexture._getFragmentCode(targetReg, regCache, sharedRegisters, uvReg);
            }
            else {
                code += this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            }
            if (index > 4)
                code += 'add ' + uvReg + '.xy, ' + uvReg + '.xy, ' + uvReg + '.zw\n' +
                    this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            if (index > 1)
                code += 'sub ' + uvReg + '.xy, ' + this._baseChunk.depthMapCoordReg + '.xy, ' + uvReg + '.zw\n' +
                    this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            if (index > 5)
                code += 'sub ' + uvReg + '.xy, ' + uvReg + '.xy, ' + uvReg + '.zw\n' +
                    this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            if (index > 2) {
                code += 'neg ' + uvReg + '.w, ' + uvReg + '.w\n' + // will be rotated 90 degrees when being accessed as wz
                    'add ' + uvReg + '.xy, ' + uvReg + '.wz, ' + this._baseChunk.depthMapCoordReg + '.xy\n' +
                    this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            }
            if (index > 6)
                code += 'add ' + uvReg + '.xy, ' + uvReg + '.xy, ' + uvReg + '.wz\n' +
                    this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            if (index > 3)
                code += 'sub ' + uvReg + '.xy, ' + this._baseChunk.depthMapCoordReg + '.xy, ' + uvReg + '.wz\n' +
                    this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            if (index > 7)
                code += 'sub ' + uvReg + '.xy, ' + uvReg + '.xy, ' + uvReg + '.wz\n' +
                    this._addSample(uvReg, targetReg, regCache, sharedRegisters);
            index -= 8;
        }
        regCache.removeFragmentTempUsage(temp);
        regCache.removeFragmentTempUsage(uvReg);
        code += 'mul ' + targetReg + '.w, ' + targetReg + '.w, ' + dataReg + '.x\n' + // average
            _super.prototype._getFragmentCode.call(this, targetReg, regCache, sharedRegisters);
        return code;
    };
    /**
     * @inheritDoc
     */
    _Shader_ShadowDitheredMethod.prototype._setRenderState = function (renderState) {
        _super.prototype._setRenderState.call(this, renderState);
        this._grainMap._setRenderState(renderState);
    };
    /**
     * Adds the code for another tap to the shader code.
     * @param uvReg The uv register for the tap.
     * @param depthMapRegister The texture register containing the depth map.
     * @param decReg The register containing the depth map decoding data.
     * @param targetReg The target register to add the tap comparison result.
     * @param regCache The register cache managing the registers.
     * @return
     */
    _Shader_ShadowDitheredMethod.prototype._addSample = function (uvReg, targetReg, regCache, sharedRegisters) {
        var temp = regCache.getFreeFragmentVectorTemp();
        regCache.addFragmentTempUsages(temp, 1);
        var code = this._baseTexture._getFragmentCode(temp, regCache, sharedRegisters, uvReg) +
            'add ' + targetReg + '.w, ' + targetReg + '.w, ' + temp + '.w\n';
        regCache.removeFragmentTempUsage(temp);
        return code;
    };
    return _Shader_ShadowDitheredMethod;
}(_Shader_ShadowMethodBase));
export { _Shader_ShadowDitheredMethod };
ShaderBase.registerAbstraction(_Shader_ShadowDitheredMethod, ShadowDitheredMethod);
